'use client';

import { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';

// Different easing functions
const easingFunctions = {
  linear: (t: number) => t,
  easeOut: (t: number) => 1 - Math.pow(1 - t, 3), // Cubic ease-out
  easeInOut: (t: number) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2),
  fastStart: (t: number) => Math.sqrt(t), // Square root for fast start, slow end
  exponential: (t: number) => (t === 1 ? 1 : 1 - Math.pow(2, -10 * t)) // Exponential ease-out
};

interface AnimatedProgressBarProps {
  startTime?: Date;
  duration?: number;
  easingType?: keyof typeof easingFunctions;
}

const DEFAULT_DURATION = 120;

export default function AnimatedProgressBar({
  startTime,
  duration,
  easingType = 'easeInOut'
}: AnimatedProgressBarProps) {
  const [progress, setProgress] = useState(1);
  const animationRef = useRef<number | null>(null);
  const initialStartTimeRef = useRef<Date | undefined>(startTime);
  const totalDuration = duration || DEFAULT_DURATION;

  useEffect(() => {
    const startAnimation = () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }

      const initialStartTime = initialStartTimeRef.current;
      const elapsedSeconds = initialStartTime
        ? Math.min(Math.floor((Date.now() - initialStartTime.getTime()) / 1000), totalDuration)
        : 0;

      const animationStartTime = Date.now();
      const remainingDuration = (totalDuration - elapsedSeconds) * 1000; // Convert to milliseconds
      const startProgress = Math.max(1, Math.min(90, (elapsedSeconds / totalDuration) * 90));

      const animate = () => {
        const currentTime = Date.now();
        const elapsed = currentTime - animationStartTime;
        const rawProgress = Math.min(elapsed / remainingDuration, 1);

        // Apply easing function
        const easedProgress = easingFunctions[easingType](rawProgress);
        const currentProgress = startProgress + (90 - startProgress) * easedProgress;

        setProgress(Math.min(90, currentProgress));

        if (rawProgress < 1 && currentProgress < 90) {
          animationRef.current = requestAnimationFrame(animate);
        }
      };

      if (startProgress < 90) {
        animationRef.current = requestAnimationFrame(animate);
      } else {
        setProgress(90);
      }
    };

    startAnimation();

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [totalDuration, easingType]);

  const progressPercentage = `${progress}%`;

  return (
    <div className="w-32 h-fit rounded-lg p-0 flex items-center">
      <div
        className="w-full bg-white/20 rounded-full h-1 overflow-hidden"
        role="progressbar"
        aria-valuemin={1}
        aria-valuemax={90}
      >
        <motion.div
          className="bg-blue-600 h-full rounded-full"
          style={{ width: progressPercentage }}
          transition={{ duration: 0.1 }}
        />
      </div>
    </div>
  );
}
